import {
  aliasOf,
  property,
  subclass,
} from "@arcgis/core/core/accessorSupport/decorators";
import { messageBundle, tsx } from "@arcgis/core/widgets/support/widget";
import Widget from "@arcgis/core/widgets/Widget";

import NavBarViewModel, {
  navbarRoute,
  sortFields,
  sortPair,
} from "./NavBar/NavBarViewModel";
import NavBarT9N from "../../t9n/ui/NavBar/NavBar.json";
import HeaderT9N from "../../t9n/ui/Header/Header.json";
import { VNode } from "src/interfaces/interfaces";

import "./NavBar/styles/NavBar.scss";
import SearchBox from "../widgets/SearchBox";
import { watch, when, whenOnce } from "@arcgis/core/core/reactiveUtils";

import { applyShadowStyle, getBaseUrl } from "../utils/utils";
import {
  ITEM_BROWSER_SCROLL_STYLE,
  SHADOW_STYLE_NAVBAR,
} from "../utils/shadowStyles";
import { addUrlParam, removeUrlParam } from "../utils/urlUtils";

import t9nAppMessages from "../../t9n/App/App.json";
import { isEnterprise } from "../utils/appUtils";

export interface NavBarProperties extends __esri.WidgetProperties {
  name?: string;
  view?: __esri.MapView | __esri.SceneView;
}

const MOBILE_VIEW_BREAK_POINT: number = 600; // (Needs to match hardcoded CSS val in Navbar.scss)

@subclass("Landing.ui.NavBar")
export default class NavBar extends Widget {
  @property()
  @messageBundle("dist/assets/t9n/ui/NavBar/NavBar")
  messages: typeof NavBarT9N;

  @property()
  @messageBundle("dist/assets/t9n/ui/Header/Header")
  headerMessages: typeof HeaderT9N;

  @property()
  @aliasOf("viewModel.appMessages")
  @messageBundle("dist/assets/t9n/App/App")
  appMessages: typeof t9nAppMessages;

  @property()
  TabStates;

  @property()
  searchBox: SearchBox = new SearchBox();

  @property()
  @aliasOf("viewModel.portal")
  portal: __esri.Portal;

  @property({
    type: NavBarViewModel,
  })
  viewModel: NavBarViewModel = new NavBarViewModel();

  @property()
  _sortTermsToSortFields: [string, sortFields][];

  @property()
  _isMobileView: boolean = false;

  @property()
  _resourceSwapModal: HTMLCalciteModalElement;

  @property()
  _newResourceItem: __esri.PortalItem | __esri.PortalGroup;

  @property()
  _newResourceType: "webmap" | "webscene" | "group";

  @property()
  _orgUrl: string;

  @property()
  modalTitle: string;

  @property()
  buttonText: string;

  @property()
  buttonIcon: string = null;

  @property({
    readOnly: true,
  })
  isOpen = false;

  @property()
  selectedGroups: __esri.PortalGroup[] = null;

  @property()
  enableFiltering = false;

  @property()
  enableSingleSelection = false;

  @property()
  isConfirmed = false;

  @property()
  ready: boolean = false;

  @property()
  content: any;

  @property()
  groupCardsDOM: any[] = null;

  @property()
  paginationInfo: {
    total: number;
    start: number;
    num: number;
  } = {
    total: undefined,
    start: 1,
    num: 60,
  };

  @property()
  itemBrowserUpdate: any;

  @property()
  browserType: "map" | "scene" | "group";

  constructor(properties?: NavBarProperties) {
    super(properties);
    window.addEventListener("resize", this._handleWindowResize.bind(this));
  }

  postInitialize() {
    this._handleWindowResize();
    when(
      () => this?.searchBox !== undefined,
      () => {
        this.viewModel.searchBox = this.searchBox;
      },
      {
        initial: true,
        once: true,
      }
    );

    this.searchBox.navbarVM = this.viewModel;
    when(
      () => this?.viewModel?.itemLoaderVM,
      () => {
        this._newResourceItem = this.viewModel.itemLoaderVM.getLoadedItem();
        this.browserType =
          this.viewModel.itemLoaderVM.loadedItemType === "group"
            ? "group"
            : this.viewModel.itemLoaderVM.loadedItemType === "webscene"
            ? "scene"
            : "map";
        this._newResourceType = this.viewModel.itemLoaderVM
          .loadedItemType as any;
      },
      {
        initial: true,
        once: true,
      }
    );

    if (!isEnterprise(this.portal)) {
      // Online Tabs
      this.TabStates = {
        browseAll: this.messages.BrowseAll,
        myApps: this.messages.MyApps,
        featured: this.messages.Featured,
      };
    } else {
      // Enterprise Tabs
      this.TabStates = {
        browseAll: this.messages.BrowseAll,
        myApps: this.messages.MyApps,
      };
    }

    // Default Tab
    this._performChangeTab(this.viewModel.navigationRoute ?? "browseAll");

    watch(
      () => this?.viewModel?.navigationRoute,
      () => {
        // Update Sort for new Tab
        const sortFieldsForNewTab = this._decideSortFieldsForTab();
        this.viewModel.setCurrentSort(sortFieldsForNewTab);
        this._sortTermsToSortFields = sortFieldsForNewTab;

        // update Search for new Tab
        this.viewModel.setCurrentSearchFilter();
        this.scheduleRender();
      },
      {
        initial: true,
      }
    );

    this._orgUrl = getBaseUrl(this.portal).replace("http://", "https://");
    this.scheduleRender();
  }

  render() {
    const search = this._renderSearch();
    const nav = this._renderNav();
    const btns = this._renderButtons();

    return [
      <div class="navbar">
        {search}
        {nav}
        {btns}
      </div>,
      this.viewModel?.onboardingModal?.render(),
      this._shouldRenderFilterChips(),
    ];
  }

  private _decideSortFieldsForTab(): sortPair[] {
    const { sortingTerms } = this.appMessages;
    switch (this.viewModel.navigationRoute) {
      case "browseAll":
        return this.viewModel.decideBrowseAllAndFeatSorts();
      case "featured":
        return this.viewModel.decideBrowseAllAndFeatSorts();
      case "myApps":
        return [
          [sortingTerms.dateModified, "modified"],
          [sortingTerms.name, "title"],
          [sortingTerms.owner, "owner"],
        ];
    }
  }

  private _renderSearch(): VNode {
    return <div class="search">{this?.searchBox?.render()}</div>;
  }

  private _renderNav(): VNode {
    return (
      <calcite-tabs
        layout="inline"
        position="top"
        class="links"
        afterCreate={(tabs) => applyShadowStyle(SHADOW_STYLE_NAVBAR, tabs)}
      >
        <calcite-tab-nav slot="title-group" class="links__tabs">
          {Object.keys(this?.TabStates)?.map((enumKey, index) => {
            return (
              <calcite-tab-title
                class="nav-option"
                key={`navOption${index}`}
                selected={
                  this.TabStates[this.viewModel.navigationRoute] ===
                  this.TabStates[enumKey]
                    ? "true"
                    : "false"
                }
                onclick={this._performChangeTab.bind(this, enumKey)}
                onkeydown={this._handleKeyboardChangeTab.bind(this, enumKey)}
              >
                {this.TabStates[enumKey]}
              </calcite-tab-title>
            );
          })}
        </calcite-tab-nav>
      </calcite-tabs>
    );
  }

  private _renderButtons(): VNode {
    return (
      <div class="buttons">
        {this._renderResourceButton()}
        {this._renderResourceSwapModal()}

        {!isEnterprise(this.portal) ? this._renderWelcomeButton() : null}
        {this._renderSort()}
      </div>
    );
  }

  private _renderWelcomeButton(): VNode {
    const { onboardingStrings } = this.appMessages;
    return (
      <calcite-button
        class="welcome-btn"
        onclick={() => this.viewModel.openOnboarding()}
        kind="neutral"
        scale="m"
        title={onboardingStrings?.launchBtnText}
        appearance="transparent"
        icon-start="mega-phone"
      >
        {this.onlyShowTextWhenNotMobile(onboardingStrings?.launchBtnText)}
      </calcite-button>
    );
  }

  private _shouldRenderFilterChips(): VNode {
    return (this.viewModel.navigationRoute === "create" ||
      this.viewModel.navigationRoute === "browseAll") &&
      this?.viewModel?.filterChips?.chips?.length > 0
      ? this.viewModel?.filterChips?.render()
      : null;
  }

  private _renderSort(): VNode {
    const { sortingTerms } = this.appMessages;
    const { sortBy } = sortingTerms;
    return (
      <calcite-dropdown
        class="sort-dropdown"
        scale="l"
        width-scale="m"
        disabled={this.viewModel.isFeaturedTab()}
        type="click"
        placement="bottom-end"
        afterCreate={this._handleDropdownSelection.bind(this)}
      >
        <calcite-button
          kind="neutral"
          appearance="transparent"
          icon-start="sort-descending"
          slot="trigger"
          alignment="center"
          scale="m"
          width="auto"
          title={sortBy}
        >
          {this.onlyShowTextWhenNotMobile(this.viewModel.selectedSortField)}
        </calcite-button>

        <calcite-dropdown-group
          selection-mode="single"
          group-title={sortBy}
          role="menu"
          scale="l"
          title={sortBy}
        >
          {this._renderDropdownItems()}
        </calcite-dropdown-group>
      </calcite-dropdown>
    );
  }

  private onlyShowTextWhenNotMobile(text: string): string | null {
    return !this._isMobileView ? text : null;
  }

  private _handleDropdownSelection(dropdown: any): void {
    dropdown.addEventListener("calciteDropdownSelect", (e: any) => {
      this.viewModel.handleSortSelectionChange(e);
    });
  }

  private _renderDropdownItems(): VNode[] {
    return this._sortTermsToSortFields?.map(
      (sortInfo: [string, sortFields], index: number) => {
        return (
          <calcite-dropdown-item
            key={`sortItem${index}`}
            role="menuitemradio"
            scale="l"
            selection-mode="single"
            tabindex="0"
            value={sortInfo[1]}
            selected={sortInfo[1] === this.viewModel.sortField}
          >
            {sortInfo[0]}
          </calcite-dropdown-item>
        );
      }
    );
  }

  private _handleKeyboardChangeTab(
    enumKey: navbarRoute,
    keyEvent: KeyboardEvent
  ) {
    if (keyEvent.code === "Enter" || keyEvent.code === "Space") {
      this._performChangeTab(enumKey);
    }
  }

  private _performChangeTab(enumKey: navbarRoute) {
    if (enumKey != null) {
      this.viewModel.setNavbarRoute(enumKey);
      this.scheduleRender();
    }
  }

  private _handleWindowResize() {
    if (window.innerWidth <= MOBILE_VIEW_BREAK_POINT) {
      this._isMobileView = true;
      this.viewModel.suggestionsButtonSignal = false;
      this.viewModel.rightSidebarSignal = false;
      this.scheduleRender();
    } else {
      this._isMobileView = false;
      this.scheduleRender();
    }
  }

  private _renderResourceButton() {
    const { buttonText, buttonTextScene, buttonTextGroup, clearMap } =
      this.appMessages.resourceSwap;
    return this?.viewModel?.itemLoaderVM?.loadedItemType === "none" ? (
      <calcite-button
        kind="neutral"
        appearance="transparent"
        icon-start="analysis"
        title={buttonText}
        disabled={this.viewModel.isFeaturedTab()}
        onclick={() => {
          this._resourceSwapModal.setAttribute("open", "true");
        }}
      >
        {this.onlyShowTextWhenNotMobile(buttonText)}
      </calcite-button>
    ) : (
      <calcite-split-button
        kind="neutral"
        appearance="transparent"
        primary-icon-start="analysis"
        title={
          this.viewModel.itemLoaderVM.loadedItemType === "group"
            ? buttonTextGroup
            : this.viewModel.itemLoaderVM.loadedItemType === "webscene"
            ? buttonTextScene
            : buttonText
        }
        disabled={this.viewModel.isFeaturedTab()}
        primary-text={this.onlyShowTextWhenNotMobile(
          this.viewModel.itemLoaderVM.loadedItemType === "group"
            ? buttonTextGroup
            : this.viewModel.itemLoaderVM.loadedItemType === "webscene"
            ? buttonTextScene
            : buttonText
        )}
        afterCreate={(splitButton: HTMLCalciteSplitButtonElement) => {
          const tryXButtonUpdate = (numOfTriesLeft: number) => {
            const xButton: HTMLCalciteButtonElement =
              splitButton.shadowRoot.querySelector(
                "calcite-button[slot='trigger']"
              );
            if (xButton != null) {
              xButton.iconStart = "x";
              xButton.title = clearMap;
            } else if (numOfTriesLeft > 0) {
              setTimeout(tryXButtonUpdate.bind(this, numOfTriesLeft - 1), 500);
            } else {
              return;
            }
          };
          setTimeout(tryXButtonUpdate.bind(this, 5), 500);
        }}
        onCalciteSplitButtonPrimaryClick={() => {
          this._resourceSwapModal.setAttribute("open", "true");
        }}
        onCalciteSplitButtonSecondaryClick={(e) => {
          window.location.assign(
            removeUrlParam(["webmap", "webscene", "group"])
          );
        }}
      ></calcite-split-button>
    );
  }

  private _renderResourceSwapModal() {
    const { map, scene, group } = this.appMessages.resourceSwap;
    return (
      <calcite-modal
        width-scale="l"
        fullscreen={true}
        afterCreate={(modal: HTMLCalciteModalElement) => {
          modal.setFocus();
          this._resourceSwapModal = modal;
          modal.beforeClose = () => {
            return new Promise((resolve) => {
              this._resourceSwapModal.removeAttribute("open");
              resolve();
            });
          };
        }}
        messageOverrides={{ close: this.appMessages.modals.closeButtonTip }}
      >
        <div slot="header" class="header-wrapper">
          {this._renderHeader()}
          <calcite-segmented-control
            slot="content-top"
            bind={this}
            scale="l"
            width="full"
            oncalciteSegmentedControlChange={(e) => {
              this.browserType = e.srcElement.value;
              this.scheduleRender();
            }}
            value={this.browserType}
          >
            <calcite-segmented-control-item
              value="map"
              checked={this.browserType === "map"}
            >
              {map}
            </calcite-segmented-control-item>
            <calcite-segmented-control-item
              value="scene"
              checked={this.browserType === "scene"}
            >
              {scene}
            </calcite-segmented-control-item>
            <calcite-segmented-control-item
              value="group"
              checked={this.browserType === "group"}
            >
              {group}
            </calcite-segmented-control-item>
          </calcite-segmented-control>
        </div>

        <div slot="content" id="resource-switch-div">
          {this.browserType === "map"
            ? this._renderItemBrowser("map")
            : this.browserType === "scene"
            ? this._renderItemBrowser("scene")
            : this.browserType === "group"
            ? this._renderGroupBrowser()
            : null}
        </div>

        <calcite-button
          key="resource-swap-confirm-button"
          bind={this}
          id="onboardingButton"
          scale="l"
          onclick={() => {
            window.location.assign(
              addUrlParam(
                this._newResourceType,
                this._newResourceItem.id,
                removeUrlParam(["webmap", "webscene", "group"])
              )
            );
          }}
          slot="primary"
          disabled={this._newResourceItem?.id == null}
          width="full"
        >
          {this.appMessages.resourceSwap.confirmText}
        </calcite-button>
        <calcite-button
          key="resource-swap-cancel-button"
          bind={this}
          id="onboardingButton"
          scale="l"
          onclick={() => {
            this._resourceSwapModal.removeAttribute("open");
          }}
          slot="secondary"
          width="full"
          appearance="outline"
        >
          {this.appMessages.resourceSwap.cancelText}
        </calcite-button>
      </calcite-modal>
    );
  }

  private _renderHeader() {
    const { modalTitle, modalTitleScene, modalTitleGroup } =
      this.appMessages.resourceSwap;
    return (
      <div class="resource-swap-header">
        <h2>
          {this.browserType === "map"
            ? modalTitle
            : this.browserType === "scene"
            ? modalTitleScene
            : this.browserType === "group"
            ? modalTitleGroup
            : null}
        </h2>
      </div>
    );
  }

  private _renderItemBrowser(type: "map" | "scene" = "map"): VNode {
    return (
      <arcgis-item-browser
        key={`item-browser-key-${type}`}
        class="resource-switch-browser"
        user={this.portal.user}
        api={4}
        portal={this.portal}
        config={{ baseUrl: `${this._orgUrl}/home/` }}
        preflight={(query: any) => {
          query.baseQuery = `(type:(${
            type === "map" ? "Web Map" : "Web Scene"
          }) -type:"Web Mapping Application")`;
          return query;
        }}
        selectedItems={
          this._newResourceItem != null ? [this._newResourceItem] : []
        }
        selection="single"
        afterCreate={(itemBrowser: any) => {
          applyShadowStyle(ITEM_BROWSER_SCROLL_STYLE, itemBrowser);
        }}
        onArcgisItemBrowserSelect={this._handleSelection.bind(this)}
        onArcgisItemBrowserUpdate={(e) => {
          this.itemBrowserUpdate = e.detail;

          if (
            this._newResourceItem != null &&
            this._newResourceType !== "group"
          ) {
            // filter Currently selected item, if it is in list to display
            this.itemBrowserUpdate.results =
              this.itemBrowserUpdate.results.filter((item) => {
                return item?.id !== this?._newResourceItem?.id;
              });
            // ALWAYS PREPEND THE SELECTED ITEM TO THE BEGINNING OF THE LIST - such that it appears on the top for every pagination page
            this.itemBrowserUpdate.results.unshift(this._newResourceItem);
            this.scheduleRender();
          }

          this.scheduleRender();
        }}
      >
        <arcgis-item-browser-top-bar slot="top-bar" filter-toggle>
          <arcgis-item-browser-bucket-select slot="bucket"></arcgis-item-browser-bucket-select>
          <arcgis-item-browser-search
            slot="search"
            term=""
          ></arcgis-item-browser-search>
        </arcgis-item-browser-top-bar>
        <arcgis-item-browser-sort
          slot="sort"
          field="modified"
        ></arcgis-item-browser-sort>
        <arcgis-item-browser-filters slot="filters">
          <arcgis-item-browser-filter-folder expanded="true"></arcgis-item-browser-filter-folder>
          <arcgis-item-browser-filter-date></arcgis-item-browser-filter-date>
          <arcgis-item-browser-filter-tags></arcgis-item-browser-filter-tags>
          <arcgis-item-browser-filter-categories></arcgis-item-browser-filter-categories>
          <arcgis-item-browser-filter-sharing></arcgis-item-browser-filter-sharing>
        </arcgis-item-browser-filters>
        <arcgis-item-browser-content slot="content" class="js-content">
          {this.itemBrowserUpdate?.results.map((item, index) => {
            return (
              <arcgis-item-browser-card
                key={`item-browser-card-${item.id}`}
                item={item}
                portal={this.portal}
                user={this.portal.user}
                selected={this._newResourceItem?.id === item?.id}
                selection="single"
                name="item-browser-demo"
              >
                <div slot="content-end" class="card-extra-content">
                  <calcite-link
                    onclick={(e: Event) => {
                      e.stopPropagation();
                    }}
                    icon-end="launch"
                    href={`${this._orgUrl}/home/item.html?id=${item?.id}`}
                    target="_blank"
                  >
                    {this.appMessages.hamburgerBtns.viewItemDetails}
                  </calcite-link>
                </div>
              </arcgis-item-browser-card>
            );
          })}
        </arcgis-item-browser-content>
        <arcgis-item-browser-pagination
          slot="pagination"
          start="1"
          num="20"
          class="js-pagination"
          total={this?.itemBrowserUpdate?.total}
        ></arcgis-item-browser-pagination>
      </arcgis-item-browser>
    );
  }

  private _handleSelection(e) {
    const resource = e?.detail?.[0];
    if(resource){
      this._newResourceItem = resource;
      this._newResourceType =
        resource?.type === "Web Map"
          ? "webmap"
          : resource?.type === "Web Scene"
          ? "webscene"
          : null;
    }
    this.scheduleRender();
  }

  private _renderGroupBrowser(): VNode {
    const { portal } = this;
    const { user, url } = portal;

    return (
      <arcgis-group-browser
        afterCreate={this._handleGroupBrowserListeners.bind(this)}
        api={4}
        class="resource-switch-browser"
        config={{ baseUrl: `${url}/home/` }}
        key={"group-browser-key"}
        portal={portal}
        preflight={this.orgIdQuery()}
        selectedGroups={
          this._newResourceItem != null ? [this._newResourceItem] : []
        }
        selection="single"
        user={user}
        view="list"
      >
        {this._renderGrpBrowserTopBar()}
        {this._renderGrpBrowserSort()}
        {this._renderGrpBrowserFilter()}
        {this._renderGrpBrowserContent()}
        {this._renderGrpBrowserPagination()}
      </arcgis-group-browser>
    );
  }

  private _renderGrpBrowserTopBar(): VNode {
    return (
      <arcgis-group-browser-top-bar slot="top-bar" filter-toggle>
        <arcgis-group-browser-search
          slot="search"
          term=""
        ></arcgis-group-browser-search>
      </arcgis-group-browser-top-bar>
    );
  }

  private _renderGrpBrowserSort(): VNode {
    return <arcgis-group-browser-sort slot="sort"></arcgis-group-browser-sort>;
  }

  private _renderGrpBrowserFilter(): VNode {
    return (
      <arcgis-group-browser-filters slot="filters">
        <arcgis-group-browser-filter-owner owner="owner"></arcgis-group-browser-filter-owner>
        <arcgis-group-browser-filter-membership></arcgis-group-browser-filter-membership>
        <arcgis-group-browser-filter-member-org></arcgis-group-browser-filter-member-org>
        <arcgis-group-browser-filter-special-groups></arcgis-group-browser-filter-special-groups>
        <arcgis-group-browser-filter-date property="modified"></arcgis-group-browser-filter-date>
        <arcgis-group-browser-filter-date property="created"></arcgis-group-browser-filter-date>
        <arcgis-group-browser-filter-sharing></arcgis-group-browser-filter-sharing>
      </arcgis-group-browser-filters>
    );
  }

  private _renderGrpBrowserContent(): VNode {
    return (
      <arcgis-group-browser-content slot="content">
        {this.groupCardsDOM}
      </arcgis-group-browser-content>
    );
  }

  private _renderGrpBrowserPagination(): VNode {
    const { total, start, num } = this.paginationInfo;
    return (
      <arcgis-group-browser-pagination
        num={num}
        slot="pagination"
        start={start}
        total={total}
      ></arcgis-group-browser-pagination>
    );
  }

  private _handleGroupBrowserListeners(gb: any) {
    gb.addEventListener("arcgisGroupBrowserUpdate", async (e: any) => {
      const groupBrowserResults = e.detail;

      if (this._newResourceItem != null && this._newResourceType === "group") {
        if (
          this._newResourceItem?.tags == null &&
          this._newResourceItem?.owner == null &&
          this._newResourceItem?.created == null &&
          this._newResourceItem?.modified == null
        ) {
          // query the portal for this group to get the full details
          const group = await this.portal.queryGroups({
            query: `id:${this._newResourceItem?.id}`,
          });
          this._newResourceItem = group.results[0];
        }

        // filter Currently selected item, if it is in list to display
        groupBrowserResults.results = groupBrowserResults.results.filter(
          (item) => {
            return item?.id !== this?._newResourceItem?.id;
          }
        );
        // ALWAYS PREPEND THE SELECTED ITEM TO THE BEGINNING OF THE LIST - such that it appears on the top for every pagination page
        groupBrowserResults.results.unshift(this._newResourceItem);
        this.scheduleRender();
      }
      this.groupCardsDOM = this.generateCardsDOM(groupBrowserResults.results);
      this.paginationInfo = groupBrowserResults;
      this.scheduleRender();
    });
    gb.addEventListener("arcgisGroupBrowserSelect", (e: any) => {
      const resource = e?.detail?.[0];
      this._selectGroup(resource?.group);
      this.emit("group-selected");
    });
  }

  isSelected(id) {
    return this?.selectedGroups?.some((group) => group.id === id);
  }

  orgIdQuery() {
    const { orgId } = this.portal.user;
    return (query) => {
      query.baseQuery = `orgid:${orgId}`;
      return query;
    };
  }

  generateCardsDOM(groups: any): VNode[] {
    return groups?.map((group: any, index: number) => {
      const { portal } = this;
      const { url, user } = portal;
      return (
        <arcgis-group-card
          baseUrl={`${url}/home/`}
          group={group}
          name={`group-browser-selector-${index}`}
          key={`group-browser-card-${index}-${group?.id}`}
          portal={portal}
          preview="top"
          selection="single"
          selected={this?._newResourceItem?.id === group?.id}
          showBadges={true}
          showOwner={true}
          user={user}
          afterCreate={(card: any) => {
            // override default click behavior to match the Item Browser UX
            card.addEventListener("arcgisGroupCardSelect", (e: any) => {
              e.stopPropagation();
              this._selectGroup(e?.detail);
              this.emit("group-selected");
            });

            // override default click behavior to match the Item Browser UX
            card.addEventListener("arcgisGroupBrowserCardPreview", (e: any) => {
              e.stopPropagation();
              card.selected = true;

              this._selectGroup(e.detail);
              this.emit("group-selected");
            });

            this.on("group-selected", () => {
              if (this?._newResourceItem?.id === card?.group?.id) {
                card.selected = true;
              } else {
                card.selected = false;
              }
            });
          }}
        ></arcgis-group-card>
      );
    });
  }

  private _selectGroup(group: __esri.PortalGroup) {
    if((group as any)?.group != null){
      group = (group as any)?.group;
    }
    this._newResourceItem = group;
    this._newResourceType = "group";
    this.scheduleRender();
  }
}
